<template>
    <button 
        ref="button" 
        class="settings-player__item" 
        @click="btnHandler" 
        @focus="onFocus"
        @blur="onBlur"
    >
        <span>{{ playerAutoplay }}</span>
        <span>{{ autoPlayState }}</span>
    </button>
</template>

<script setup lang="ts">
    import { Strings } from "@/locales/types/enums/strings";
    import { useI18n } from "vue-i18n";
    import { type Ref, ref, computed, onMounted, onBeforeUnmount } from "vue";
    import { useUserSettingsStore } from "@/stores/UserSettings";
    import { storeToRefs } from 'pinia';
    import { useRouter } from 'vue-router';
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";

    const router = useRouter();
    const UserSettingsStoreModule = useUserSettingsStore();
    const { SetEnabledAutoplay } = UserSettingsStoreModule;
    const {
        getEnabledAutoplay: isEnableAutoplay
    } = storeToRefs(UserSettingsStoreModule);

    const { t } = useI18n();
    const emit = defineEmits(["ready", "start"]);
    const { $navigator } = useNuxtApp();
    const playerAutoplay: string = t(Strings.SETTINGS_PLAYER_AUTOPLAY).toString();
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const button: Ref<HTMLButtonElement | null> = ref(null);

    const autoPlayState = computed((): string => isEnableAutoplay.value ? t(Strings.SETTINGS_PLAYER_ON).toString() : t(Strings.SETTINGS_PLAYER_OFF).toString());

    const onFocus = (): void => {
        registerKeyListeners();
    };
    const onBlur = (): void => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = () => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:left", onKeyLeft);
            $navigator.on("keypress:back", onKeyBack);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:left", onKeyLeft);
            $navigator.off("keypress:back", onKeyBack);
            isRegKeyListeners.value = false;
        }
    };
    const btnHandler = () => {
        SetEnabledAutoplay(!isEnableAutoplay.value);
    };
    const onKeyBack = () => {
        goToBack();
    };
    const goToBack = (): void => {
        router.push(linkItems.settings.routerPath);
    };
    const onKeyLeft = (event: KeyboardEvent): void => {
        const element = event.target as HTMLElement;
        if (element.getAttribute("data-type") === "modal-button") {
            return;
        }
        goToBack();
    };
    const setSelfFocused = (): void => {
        button.value?.focus();
        emit("ready");
    };
    const unsetSelfFocused = (): void => {
        unregisterKeyListeners();
    };

    const start = () => {
        emit("start");
    };

    start();

    onMounted(() => {
        setSelfFocused();
    });

    onBeforeUnmount(() => {
        unsetSelfFocused();
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>